import React from 'react';
import { Box, Typography, Grid, TextField, Checkbox, FormControlLabel, Paper, Divider, CircularProgress } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { getDays } from 'core/API/organizationSettings';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useAuth } from 'module/auth/context/AuthContext';
import { UserAvailability } from 'core/interfaces/userInterface';

interface AvailabilityFormProps {
	onSubmit: (values: any) => Promise<void>;
	onCancel: () => void;
	formRef?: React.MutableRefObject<any>;
}

const validationSchema = Yup.object({
	availability: Yup.array(),
	notes: Yup.string().required('Reason for change is required'),
});

const AvailabilityForm: React.FC<AvailabilityFormProps> = ({ onSubmit, onCancel: _onCancel, formRef }) => {
	const { showToast } = useToastContext();
	const { user } = useAuth();

	// Fetch days for availability picker
	const { data: daysData, isLoading: daysLoading } = useQuery(['days'], () => getDays());
	// console.log('Days data:', daysData);
	// Get user's current availability
	const currentAvailability: UserAvailability[] = React.useMemo(() => 
		user?.userAvailability || [], 
		[user?.userAvailability]
	);

	// Store initial values in a ref to compare for changes
	const initialValuesRef = React.useRef<any>(null);

	const handleSubmit = async (values: any) => {
		// Validate that at least one day is selected
		const enabledDays = values.availability.filter((d: any) => d.enabled);
		
		if (enabledDays.length === 0) {
			showToast('error', 'Please select at least one day');
			return;
		}

		// Validate that all enabled days have start and end times
		const invalidDays = values.availability.filter(
			(d: any) => d.enabled && (!d.startTime || !d.endTime)
		);

		if (invalidDays.length > 0) {
			const dayNames = invalidDays.map((d: any) => d.day.label).join(', ');
			showToast('error', `Please set start and end times for: ${dayNames}`);
			return;
		}

		// Only send requests for days that have changed
		const changedDays = values.availability.filter((d: any, index: number) => {
			const initial = initialValuesRef.current?.availability[index];
			if (!initial) return d.enabled; // If no initial, include if enabled
			
			// Check if enabled status changed
			if (d.enabled !== initial.enabled) return true;
			
			// If enabled, check if times changed
			if (d.enabled) {
				const startChanged = d.startTime && initial.startTime 
					? !dayjs(d.startTime).isSame(dayjs(initial.startTime), 'minute')
					: d.startTime !== initial.startTime;
				const endChanged = d.endTime && initial.endTime
					? !dayjs(d.endTime).isSame(dayjs(initial.endTime), 'minute')
					: d.endTime !== initial.endTime;
				return startChanged || endChanged;
			}
			
			return false;
		});

		if (changedDays.length === 0) {
			showToast('success', 'No changes detected');
			return;
		}

		// Transform the data to match the expected format (only changed days)
		const availabilityRequests = changedDays
			.filter((d: any) => d.enabled)
			.map((d: any) => ({
				day: d.day.id,
				startTime: d.startTime ? dayjs(d.startTime).format('HH:mm:ss') : null,
				endTime: d.endTime ? dayjs(d.endTime).format('HH:mm:ss') : null,
			}));

		await onSubmit({
			availabilityRequests,
			notes: values.notes,
		});
	};

	const initialValues = React.useMemo(() => ({
		availability: (daysData || []).map(day => {
			// Try to prefill from existing availability
			// Look for availability entries that match this day of week
			const matchingAvail = currentAvailability.find((avail: UserAvailability) => {
				// const availStart = dayjs(avail.startAt);
				// // Get day of week (0=Sunday, 1=Monday, etc.)
				// const availDayOfWeek = availStart.day();
				// // Match day names (assuming day.label is like "Monday", "Tuesday", etc.)
				
				// const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
				// console.log("checking ", dayNames[availDayOfWeek], availDayOfWeek, " against ", day.label);
				return avail.day === (day?.['@id'] || avail.day === `/api/days/${day.id}`);
			});
	

			if (matchingAvail) {
				// Prefill with the time from matching availability
				const startTime = dayjs(matchingAvail.startAt);
				const endTime = dayjs(matchingAvail.endAt);
				
				return {
					day,
					enabled: true,
					startTime: startTime,
					endTime: endTime,
				};
			}

			return {
				day,
				enabled: false,
				startTime: null,
				endTime: null,
			};
		}),
		notes: currentAvailability.length > 0 
			? 'Updating existing availability' 
			: '',
	}), [daysData, currentAvailability]);
	console.log("Initial Values:", initialValues.availability);

	// Store initial values for comparison
	React.useEffect(() => {
		if (!initialValuesRef.current && initialValues.availability.length > 0) {
			initialValuesRef.current = JSON.parse(JSON.stringify(initialValues));
		}
	}, [initialValues]);

	return (
		<Formik
			innerRef={formRef}
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, setFieldValue, errors, touched }) => (
				<Form>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Box sx={{ mt: 3 }}>
							{daysLoading ? (
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
									<CircularProgress size={20} color="inherit" />
									<Typography sx={{ ml: 2 }}>Loading availability options...</Typography>
								</Box>
							) : (
								<>
							{/* Display Current Availability Section */}
							{currentAvailability.length > 0 && (
								<Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#f5f5f5' }}>
									<Typography variant="h6" gutterBottom color="primary">
										Current Availability
									</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
										Your existing availability schedule
									</Typography>
									<Divider sx={{ mb: 2 }} />
									{currentAvailability
										.filter((avail) => avail.day != null) // Only show availability with day property
										.reduce((acc, avail) => {
											// Keep only the latest availability for each day (highest id = most recent)
											const dayId = typeof avail.day === 'string' ? avail.day : avail.day!['@id'];
											const existing = acc.find(a => {
												const existingDayId = typeof a.day === 'string' ? a.day : a.day!['@id'];
												return existingDayId === dayId;
											});
											
											if (!existing) {
												acc.push(avail);
											} else if (avail.id > existing.id) {
												// Replace with newer availability (higher id)
												const index = acc.indexOf(existing);
												acc[index] = avail;
											}
											
											return acc;
										}, [] as UserAvailability[])
										.sort((a, b) => {
											// Sort by day order (position) from daysData
											if (!a.day || !b.day) return 0;
											const aDayId = typeof a.day === 'string' ? a.day : a.day['@id'];
											const bDayId = typeof b.day === 'string' ? b.day : b.day['@id'];
											const aDayData = daysData?.find((d: any) => d['@id'] === aDayId || `/api/days/${d.id}` === aDayId);
											const bDayData = daysData?.find((d: any) => d['@id'] === bDayId || `/api/days/${d.id}` === bDayId);
											const aPosition = aDayData?.position ? parseInt(aDayData.position) : 999;
											const bPosition = bDayData?.position ? parseInt(bDayData.position) : 999;
											return aPosition - bPosition;
										})
										.map((avail, index) => {
											// Get day name from day property using daysData
											if (!avail.day) return null; // Type guard
											const dayId = typeof avail.day === 'string' ? avail.day : avail.day['@id'];
											const dayData = daysData?.find((d: any) => d['@id'] === dayId || `/api/days/${d.id}` === dayId);
											const dayName = dayData?.label || 'Unknown';
											
											const startTime = dayjs(avail.startAt).format('HH:mm');
											const endTime = dayjs(avail.endAt).format('HH:mm');
											
											return (
												<Box 
													key={avail.id || index} 
													sx={{ 
														mb: 1.5, 
														p: 1.5, 
														backgroundColor: 'white',
														borderRadius: 1,
														border: '1px solid #e0e0e0'
													}}
												>
													<Typography variant="body2" fontWeight="medium">
														{dayName} → {startTime} - {endTime}
													</Typography>
												</Box>
											);
										})}
								</Paper>
							)}

							{currentAvailability.length === 0 && (
								<Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#fff3e0' }}>
									<Typography variant="body2" color="text.secondary">
										No current availability found. Set your availability below.
									</Typography>
								</Paper>
							)}

							<Typography variant="h6" gutterBottom>
								Update Your Availability
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
								Select the days you are available and specify your preferred time range.
							</Typography>
							
							{values.availability.map((d: any, index: number) => (
								<Grid
									container
									spacing={2}
									alignItems="center"
									key={d.day['@id']}
									sx={{
										mb: 1,
										borderRadius: 1,
										p: 1,
									}}
								>
									<Grid item xs={4}>
										<FormControlLabel
											control={
												<Checkbox 
													checked={d.enabled} 
													onChange={(e) => {
														const updated = [...values.availability];
														updated[index].enabled = e.target.checked;
														
														if (!e.target.checked) {
															updated[index].startTime = null;
															updated[index].endTime = null;
														} else {
															const lastFilledDay = updated
																.slice(0, index)
																.reverse()
																.find((day: any) => day.enabled && day.startTime && day.endTime);
															
															if (lastFilledDay) {
																updated[index].startTime = dayjs(lastFilledDay.startTime);
																updated[index].endTime = dayjs(lastFilledDay.endTime);
															} else {
																// showToast('success', `Please set start and end times for ${updated[index].day.label}`);
															}
														}
														setFieldValue('availability', updated);
													}} 
												/>
											}
											label={d.day.label}
										/>
									</Grid>

									<Grid item xs={4}>
										<TimePicker
											label="Start Time"
											value={d.startTime}
											onChange={(newTime) => {
												const updated = [...values.availability];
												updated[index].startTime = newTime;
												setFieldValue('availability', updated);
											}}
											disabled={!d.enabled}
											slotProps={{ 
												textField: { 
													fullWidth: true
												} 
											}}
										/>
									</Grid>

									<Grid item xs={4}>
										<TimePicker
											label="End Time"
											value={d.endTime}
											onChange={(newTime) => {
												const updated = [...values.availability];
												updated[index].endTime = newTime;
												setFieldValue('availability', updated);
											}}
											disabled={!d.enabled}
											slotProps={{ 
												textField: { 
													fullWidth: true
												} 
											}}
										/>
									</Grid>
								</Grid>
							))}

							<TextField
								fullWidth
								multiline
								rows={3}
								label="Reason for Change"
								value={values.notes}
								onChange={(e) => setFieldValue('notes', e.target.value)}
								required
								error={touched.notes && Boolean(errors.notes)}
								helperText={touched.notes && errors.notes}
								sx={{ mt: 2 }}
							/>
							</>
							)}
						</Box>
					</LocalizationProvider>
				</Form>
			)}
		</Formik>
	);
};

export default AvailabilityForm;
